/**
 * BodyTemplateField — native textarea + grouped variable-token picker.
 * Extracted from webhook-fields.tsx to keep that file under ~200 lines.
 */
import { useRef, useState } from 'react';
import { Button } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { WebhookTemplatePicker } from './WebhookTemplatePicker';

// ---- Template variable catalog -------------------------------------------

interface VarGroup {
  heading: string;
  vars: string[];
}

const TEMPLATE_VAR_GROUPS: VarGroup[] = [
  {
    heading: 'Always available',
    vars: ['alarm_name', 'alarm_id', 'watchpoint_id', 'severity', 'type', 'timestamp'],
  },
  {
    heading: 'Uptime monitors (http/https/tcp/dns/tls)',
    vars: ['status_code', 'latency_ms', 'is_up', 'error_message'],
  },
  {
    heading: 'Data monitors (waap/delivery/elastic)',
    vars: ['count', 'matched_items', 'metadata'],
  },
];

// ---- Component ------------------------------------------------------------

interface Props {
  value: string;
  currentHeaders: Record<string, string>;
  onChange: (v: string) => void;
  onApplyTemplate: (body: string, mergedHeaders: Record<string, string>) => void;
}

export function BodyTemplateField({ value, currentHeaders, onChange, onApplyTemplate }: Props) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const insertVar = (varName: string) => {
    const token = `{{${varName}}}`;
    const el = textareaRef.current;
    if (el) {
      const start = el.selectionStart ?? value.length;
      const end = el.selectionEnd ?? value.length;
      onChange(value.slice(0, start) + token + value.slice(end));
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(start + token.length, start + token.length);
      });
    } else {
      onChange(value + token);
    }
    setPickerOpen(false);
  };

  return (
    <>
      <WebhookTemplatePicker
        currentBody={value}
        currentHeaders={currentHeaders}
        onApply={onApplyTemplate}
      />
      <Field
        label="Body template (optional)"
        description="Missing variables render empty. Numbers are unquoted. Objects/arrays render as JSON."
      >
        {() => (
          <div style={{ display: 'grid', gap: 'var(--vn-space-2)', maxWidth: 480 }}>
            <textarea
              ref={textareaRef}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder={'{\n  "alarm": "{{alarm_name}}",\n  "severity": "{{severity}}"\n}'}
              rows={6}
              spellCheck={false}
              style={{
                width: '100%',
                padding: 'var(--vn-space-2) var(--vn-space-3)',
                fontFamily: 'var(--vn-font-mono)',
                fontSize: 'var(--vn-font-size-sm)',
                color: 'var(--vn-fg-default)',
                background: 'var(--vn-bg-surface)',
                border: '1px solid var(--vn-border-default)',
                borderRadius: 'var(--vn-radius-md)',
                resize: 'vertical',
                lineHeight: 1.6,
                outline: 'none',
              }}
            />

            <div style={{ position: 'relative' }}>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setPickerOpen((o) => !o)}
                style={{ fontSize: 'var(--vn-font-size-xs)' }}
              >
                Insert variable
              </Button>

              {pickerOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    zIndex: 'var(--vn-z-popover)',
                    marginTop: 'var(--vn-space-1)',
                    background: 'var(--vn-bg-surface)',
                    border: '1px solid var(--vn-border-default)',
                    borderRadius: 'var(--vn-radius-md)',
                    boxShadow: 'var(--vn-shadow-md)',
                    minWidth: 280,
                    maxHeight: 320,
                    overflowY: 'auto',
                    padding: 'var(--vn-space-2)',
                  }}
                >
                  {TEMPLATE_VAR_GROUPS.map((group) => (
                    <div key={group.heading} style={{ marginBottom: 'var(--vn-space-2)' }}>
                      <p
                        style={{
                          margin: '0 0 var(--vn-space-1)',
                          fontSize: 'var(--vn-font-size-xs)',
                          fontWeight: 600,
                          color: 'var(--vn-fg-muted)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          padding: '0 var(--vn-space-2)',
                        }}
                      >
                        {group.heading}
                      </p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-1)', padding: '0 var(--vn-space-1)' }}>
                        {group.vars.map((v) => (
                          <button
                            key={v}
                            type="button"
                            onClick={() => insertVar(v)}
                            style={{
                              padding: 'var(--vn-space-1) var(--vn-space-2)',
                              background: 'var(--vn-accent-subtle)',
                              border: '1px solid var(--vn-border-default)',
                              borderRadius: 'var(--vn-radius-sm)',
                              fontFamily: 'var(--vn-font-mono)',
                              fontSize: 'var(--vn-font-size-xs)',
                              color: 'var(--vn-fg-default)',
                              cursor: 'pointer',
                            }}
                          >
                            {`{{${v}}}`}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </Field>
    </>
  );
}
